import {defineStore} from 'pinia';
import {computed, watch} from 'vue';
import {MarkOwnerType} from '@/core/interface/api-interface';
import type {AddressMapping} from '@/core/google/address-mapping';
import {
    emailSchema,
    ownerTypeSchema,
    ownerNamesSchema,
    mobileNumberSchema,
    countrySchema,
    addressSchema,
    suburbSchema,
    stateRegionSchema,
    postCodeSchema, businessNumberSchema
} from '@/components/contact-view/ownerform';
import {useFormField} from '@/store/form-field';
import {useApplicationStore} from '@/store/application';
import LocalStorageDecorator from '@/store/storage/local-storage-decorator';
import { useAnalytics } from '@/composables/useAnalytics';
import useCountry from '@/composables/countryDependable';
import { CountryCode } from '@/config/country/type';

export interface OwnerStore {
    ownerType: MarkOwnerType | null;
    ownerNames: string[];
    email: string;
    mobileNumber: string;
    country: string;
    address: string;
    suburb: string;
    stateRegion: string;
    postCode: string;
    businessNumber: string;
}

export const useOwnerStore = defineStore('ownerStore', () => {
    const {value: email, isValid: emailIsValid, error: emailError, validate: emailValidate, reset: emailReset} = useFormField('', emailSchema);
    const {value: ownerType, isValid: ownerTypeIsValid, error: ownerTypeError, validate: ownerTypeValidate, reset: ownerTypeReset} = useFormField(null as MarkOwnerType|null, ownerTypeSchema);
    const {value: ownerNames, isValid: ownerNamesIsValid, error: ownerNamesError, validate: ownerNamesValidate, reset: ownerNamesReset} = useFormField<string[]>([''], ownerNamesSchema);
    const {value: mobileNumber, isValid: mobileNumberIsValid, error: mobileNumberError, validate: mobileNumberValidate, reset: mobileNumberReset} = useFormField('', mobileNumberSchema);
    const {value: country, isValid: countryIsValid, error: countryError, validate: countryValidate, reset: countryReset} = useFormField('', countrySchema);
    const {value: address, isValid: addressIsValid, error: addressError, validate: addressValidate, reset: addressReset} = useFormField('', addressSchema);
    const {value: suburb, isValid: suburbIsValid, error: suburbError, validate: suburbValidate, reset: suburbReset} = useFormField('', suburbSchema);
    const {value: stateRegion, isValid: stateRegionIsValid, error: stateRegionError, validate: stateRegionValidate, reset: stateRegionReset} = useFormField('', stateRegionSchema);
    const {value: postCode, isValid: postCodeIsValid, error: postCodeError, validate: postCodeValidate, reset: postCodeReset} = useFormField('', postCodeSchema);
    const {value: businessNumber, isValid: businessNumberIsValid, error: businessNumberError, reset: businessNumberReset} = useFormField('', businessNumberSchema);

    const applicationStore = useApplicationStore();
    const analytics = useAnalytics();
    const appCountry = useCountry();

    function resetData() {
        emailReset();
        ownerTypeReset();
        ownerNamesReset();
        mobileNumberReset();
        countryReset();
        addressReset();
        suburbReset();
        stateRegionReset();
        postCodeReset();
        businessNumberReset();
    }

    function importData(data: OwnerStore) {
        ownerType.value = data.ownerType;
        ownerNames.value = data.ownerNames;
        email.value = data.email;
        mobileNumber.value = data.mobileNumber;
        country.value = data.country;
        address.value = data.address;
        suburb.value = data.suburb;
        stateRegion.value = data.stateRegion;
        postCode.value = data.postCode;
        businessNumber.value = data.businessNumber;
    }

    function exportData() {
        return {
            ownerType: ownerType.value,
            ownerNames: ownerNames.value,
            email: email.value,
            mobileNumber: mobileNumber.value,
            country: country.value,
            address: address.value,
            suburb: suburb.value,
            stateRegion: stateRegion.value,
            postCode: postCode.value,
            businessNumber: businessNumber.value
        };
    }

    const isOwnerStepValid = computed(() => {
        return emailIsValid.value
            && ownerTypeIsValid.value
            && ownerNamesIsValid.value
            && mobileNumberIsValid.value
            && countryIsValid.value
            && addressIsValid.value
            && suburbIsValid.value
            && stateRegionIsValid.value
            && postCodeIsValid.value;
    });

    const ownerDetails = computed(() => ({
        email: email.value,
        ownerType: ownerType.value,
        ownerNames: ownerNames.value,
        country: country.value,
        address: address.value,
        suburb: suburb.value,
        city: appCountry.code === CountryCode.NZ ? stateRegion.value : '',
        stateRegion: appCountry.code === CountryCode.NZ ? '' : stateRegion.value,
        postCode: postCode.value,
        businessNumber: businessNumber.value,
        phone: mobileNumber.value
    }));

    watch(() => applicationStore.hasValidation, (newValue) => {
        if (newValue) {
            emailValidate();
            ownerTypeValidate();
            ownerNamesValidate();
            mobileNumberValidate();
            countryValidate();
            addressValidate();
            suburbValidate();
            stateRegionValidate();
            postCodeValidate();
        }
    })

    const setAddress = (value: AddressMapping) => {
        address.value = value.getAddress();
        suburb.value = value.getSuburb();
        stateRegion.value = value.getStateRegionCity();
        postCode.value = value.getPostcode();
    }

    const addOwnerName = (name: string) => {
        ownerNames.value.push(name);
    }

    const removeOwnerName = (index: number) => {
        ownerNames.value.splice(index, 1);
    }

    watch(isOwnerStepValid, (valid) => {
        if (valid) {
            analytics.sendEvent('card_completed_owner');
        }
    });

    return {
        isOwnerStepValid,
        setAddress,
        addOwnerName,
        removeOwnerName,
        ownerDetails,
        resetData,
        importData,
        exportData,
        email,
        ownerType,
        ownerNames,
        mobileNumber,
        country,
        address,
        suburb,
        stateRegion,
        postCode,
        businessNumber,
        emailError,
        ownerTypeError,
        ownerNamesError,
        mobileNumberError,
        countryError,
        addressError,
        suburbError,
        stateRegionError,
        postCodeError,
        businessNumberError,
        emailIsValid,
        ownerTypeIsValid,
        ownerNamesIsValid,
        mobileNumberIsValid,
        countryIsValid,
        addressIsValid,
        suburbIsValid,
        stateRegionIsValid,
        postCodeIsValid,
        businessNumberIsValid,
    };
},{
    persist: {
        storage: LocalStorageDecorator
    },
});